import React, { forwardRef } from "react";

import { useField, useFormikContext, getIn } from "formik";
import { dissoc } from "ramda";

import { Slider, SliderProps } from "src/components/Slider";

type SliderValue = number | number[];

export interface FormikSliderProps extends Omit<
  SliderProps,
  "name" | "onChange"
> {
  /** Formik field name. */
  name: string;
  /** Callback fired when the value changes. Also supports onChange for compatibility. */
  onChange?: (value: SliderValue) => void;
}

const FormikSlider = forwardRef<
  React.ComponentRef<typeof Slider>,
  FormikSliderProps
>(
  (
    {
      name,
      onValueChange: onValueChangeProp,
      onChange: onChangeProp,
      ...otherProps
    },
    ref
  ) => {
    const [field, meta, { setValue, setTouched }] = useField(name);
    const { status = {}, setStatus } = useFormikContext();
    const fieldStatus = getIn(status, name);

    return (
      <Slider
        ref={ref}
        error={meta.error || fieldStatus || ""}
        name={field.name}
        value={field.value}
        onBlur={() => setTouched(true)}
        onValueChange={(value: SliderValue) => {
          setStatus(dissoc(name, status));
          setValue(value);
          onValueChangeProp?.(value);
          onChangeProp?.(value);
        }}
        {...otherProps}
      />
    );
  }
);

FormikSlider.displayName = "FormikSlider";

export { FormikSlider };
